'use client';

import { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { getTracingDomains } from '@bts-web/utils-next-infrastructure/monitoring';

export type UserCookiePreferences = {
  functional?: boolean;
  marketing?: boolean;
  performance?: boolean;
};

export const initDataDogRum = (props?: {
  cookiePreferences?: UserCookiePreferences;
}) => {
  const { functional, marketing, performance } = props?.cookiePreferences ?? {};

  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APP_ID;

  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;

  const service = process.env.NEXT_PUBLIC_DATADOG_RUM_SERVICE;

  const env = process.env.NEXT_PUBLIC_DATADOG_ENV;

  const version = process.env.NEXT_PUBLIC_APP_VERSION;

  const apiUrl = process.env.NEXT_PUBLIC_GQL_API_ENDPOINT_URL;

  /** Defined in Vercel  */
  const optionalRumEndpoint =
    env !== 'preview' && process.env.NEXT_PUBLIC_DATADOG_API_ENDPOINT;

  if (
    applicationId &&
    clientToken &&
    env &&
    apiUrl &&
    version &&
    !isDatadogRumInitialized()
  ) {
    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.eu',
      ...(optionalRumEndpoint && { proxy: optionalRumEndpoint }),
      service,
      env,
      version,
      sessionSampleRate: marketing ? 100 : 0,
      sessionReplaySampleRate: marketing ? 20 : 0,
      traceSampleRate: 50,
      trackUserInteractions: functional ?? false,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: performance ? 'allow' : 'mask-user-input',
      storeContextsAcrossPages: true,
      allowedTracingUrls: getAllowedTracingUrls(),
      beforeSend: (event) => {
        // Suppress NEXT_REDIRECT errors
        if (
          event.type === 'error' &&
          event.error?.message === 'NEXT_REDIRECT'
        ) {
          return false;
        }

        // TODO: Check whether Next.js RSC navigation `Failed to fetch` noise
        // appears in production Datadog RUM and add a beforeSend filter if it does.
        return true;
      },
    });
  }
};

/**
 * Returns allowed tracing URLs for Datadog RUM.
 * Uses all allowed domains for both RUM and OTEL consistency.
 */
const getAllowedTracingUrls = () => {
  const domains = getTracingDomains();

  return domains.map((domain) => ({
    match: (url: string) => url.startsWith(domain),
    propagatorTypes: ['tracecontext' as const],
  }));
};

export const setDataDogUser = (userId: string) => {
  const currentUser = datadogRum.getUser();

  if (currentUser && currentUser.id === userId) {
    return;
  }

  datadogRum.setUser({ id: userId });
};

export const isDatadogRumInitialized = (): boolean => {
  try {
    const internalContext = datadogRum.getInternalContext();

    return !!internalContext?.session_id;
  } catch (error) {
    console.error('Error checking Datadog RUM initialization:', error);

    return false;
  }
};

const DatadogRumInit = (props?: {
  cookiePreferences?: UserCookiePreferences;
}) => {
  useEffect(() => {
    if (props?.cookiePreferences) {
      initDataDogRum(props);
    }
  }, []);

  return null;
};

export { DatadogRumInit };
